import { useEffect, useState } from 'react';
import {
  EntityUIState,
  useAbortController,
  useObservableState,
  useQuery
} from '@hevoio/hevo-ui-toolkit';
import { SolutionRequest, QueryType } from '../interfaces/query';
import useService from '../../../injector/hooks/useService';
import { SupportService } from '../../../support/interfaces/support';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import { ContactSupportAITrackers } from '../trackers/contact-support-ai-trackers';
import { ContactSupportAIAPI } from '../api/ContactSupportAI';

export function useContactSupportAI({ onClose }: { onClose: () => void }) {
  const { signal, abort } = useAbortController();
  const [maximized, setMaximized] = useState(false);
  const { eventTrack } = useAnalyticsTracker();
  const supportService = useService(SupportService);
  const [supportUnreadCount] = useObservableState(supportService.unreadCount$);
  const [query, setQuery] = useState<SolutionRequest | undefined>(undefined);
  const [showContactSupportOptions, setShowContactSupportOptions] = useState(false);

  useEffect(() => {
    eventTrack({
      action: ContactSupportAITrackers.CONTACT_SUPPORT_AI_OPEN
    });
  }, []);

  const {
    data,
    uiState,
    error,
    fetch: onGetSolution,
    reset: resetQuery
  } = useQuery({
    dataSource$: (params: SolutionRequest) => {
      setQuery(params);
      eventTrack({
        action: ContactSupportAITrackers.CONTACT_SUPPORT_AI_QUERY_SUBMIT,
        properties: {
          entityType: params.entityType,
          category: params.category,
          issueDescription: params.issueDescription
        }
      });

      return ContactSupportAIAPI.getSolution(params, { networkOptions: { signal: signal } }).then(
        response => {
          eventTrack({
            action: ContactSupportAITrackers.CONTACT_SUPPORT_AI_QUERY_SUCCESS,
            properties: {
              entityType: params.entityType,
              category: params.category,
              issueDescription: params.issueDescription
            }
          });
          return response;
        }
      );
    },
    fetchError: () => {
      eventTrack({
        action: ContactSupportAITrackers.CONTACT_SUPPORT_AI_QUERY_ERROR
      });
    }
  });

  const {
    uiState: ticketUIState,
    fetch: onRaiseTicketFetch
  } = useQuery({
    dataSource$: () => {
      return ContactSupportAIAPI.raiseTicket({ ...query, aiAnswer: data });
    },
    fetchError: () => {
      eventTrack({
        action: ContactSupportAITrackers.CONTACT_SUPPORT_AI_TICKET_ERROR
      });
    },
    fetchSuccess: () => {
      eventTrack({
        action: ContactSupportAITrackers.CONTACT_SUPPORT_AI_TICKET_SUCCESS
      });
      onClose();
    }
  });

  const {
    uiState: conversationUIState,
    fetch: onCreateConversation,
    reset: resetConversation
  } = useQuery({
    dataSource$: () => {
      setShowContactSupportOptions(false);
      return ContactSupportAIAPI.createConversation({ ...query, aiAnswer: data });
    },
    fetchError: () => {
      onTalkToSupportClick();
    },
    fetchSuccess: () => {
      onTalkToSupportClick();
    }
  });

  const onTalkToSupportClick = async () => {
    eventTrack({
      action: ContactSupportAITrackers.CONTACT_SUPPORT_AI_TALK_TO_SUPPORT,
      properties: {
        ...query
      }
    });
    supportService.show();
    onClose();
  };

  const onOpenSupportChatClick = () => {
    eventTrack({
      action: ContactSupportAITrackers.CONTACT_SUPPORT_AI_OPEN_SUPPORT_CHAT
    });
    supportService.show();
    onClose();
  };

  const onCloseClick = () => {
    eventTrack({
      action: ContactSupportAITrackers.CONTACT_SUPPORT_AI_CLOSE
    });
    onClose();
  };

  const onResolveIssueClick = () => {
    eventTrack({
      action: ContactSupportAITrackers.CONTACT_SUPPORT_AI_RESOLVE_ISSUE_SUCCESS,
      properties: {
        query: query
      }
    });
    resetConversation();
    resetQuery();
  };

  const onBack = () => {
    eventTrack({
      action: ContactSupportAITrackers.CONTACT_SUPPORT_AI_BACK
    });
    abort();
    resetConversation();
    resetQuery();
    setShowContactSupportOptions(false);
  };

  const onMaximize = () => {
    eventTrack({
      action: ContactSupportAITrackers.CONTACT_SUPPORT_AI_MAXIMIZE_SCREEN
    });
    setMaximized(true);
  };

  const onMinimize = () => {
    eventTrack({
      action: ContactSupportAITrackers.CONTACT_SUPPORT_AI_MINIMIZE_SCREEN
    });
    setMaximized(false);
  };

  const showQueryContainer =
    ([EntityUIState.NEW, EntityUIState.INIT].includes(uiState) && !data) ||
    (error && error.name === 'CanceledError');

  const showConversationLoader =
    conversationUIState === EntityUIState.LOADING || ticketUIState === EntityUIState.LOADING;

  let loaderTitle = undefined;
  if (conversationUIState === EntityUIState.LOADING) {
    loaderTitle = 'Creating Conversation...';
  } else if (ticketUIState === EntityUIState.LOADING) {
    loaderTitle = 'Creating Ticket...';
  }

  const showReportIssueContainer =
    !showQueryContainer && !showConversationLoader && !showContactSupportOptions;

  const onRaiseTicket = () => {
    onRaiseTicketFetch();
  };

  const openSupportOptions = () => {
    setShowContactSupportOptions(true);
  };

  const queryType = query?.entityType ? QueryType.ISSUE : QueryType.QUESTION;

  return {
    data,
    error,
    loaderTitle,
    maximized,
    onBack,
    onCloseClick,
    onCreateConversation,
    onGetSolution,
    onMaximize,
    onMinimize,
    onOpenSupportChatClick,
    onRaiseTicket,
    onResolveIssueClick,
    onTalkToSupportClick,
    openSupportOptions,
    queryType,
    showContactSupportOptions,
    showConversationLoader,
    showQueryContainer,
    showReportIssueContainer,
    supportUnreadCount,
    uiState
  };
}
